export type SotTranscriptTurnDisplay = {
    readonly speaker: string;
    readonly text: string;
    readonly start_ms?: number | null;
    readonly end_ms?: number | null;
    readonly channel?: number | null;
    readonly confidence?: number | null;
    readonly words?: ReadonlyArray<unknown>;
};

export function formatSotOffset(ms: number | null | undefined) {
    if (typeof ms !== 'number') {
        return '—';
    }
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function formatSotSpeaker(
    speaker: string,
    options: { postTransfer: boolean } = { postTransfer: false },
) {
    switch (speaker) {
        case 'agent':
            return options.postTransfer ? 'Representative' : 'Phoebe';
        case 'caller':
            return 'Caller';
        case 'unknown':
            return 'Unknown';
        default:
            if (speaker.startsWith('speaker_')) {
                return `Speaker ${speaker.replace('speaker_', '')}`;
            }
            if (speaker.startsWith('channel_')) {
                return `Channel ${speaker.replace('channel_', '')}`;
            }
            return speaker;
    }
}

export function sotSpeakerBadgeClassName(
    speaker: string,
    options: { postTransfer: boolean } = { postTransfer: false },
) {
    switch (speaker) {
        case 'agent':
            return options.postTransfer
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800';
        case 'caller':
            return 'border-sky-200 bg-sky-50 text-sky-800';
        default:
            return 'border-slate-200 bg-slate-50 text-slate-700';
    }
}

export function formatSotChannelMode(mode: string) {
    switch (mode) {
        case 'multichannel':
            return 'per-channel SOT';
        case 'diarized_mono':
            return 'diarized SOT';
        case 'mono':
            return 'mono SOT';
        case 'flat':
            return 'flat SOT';
        default:
            return 'SOT';
    }
}

// True when `turn` starts after the call was transferred, regardless of
// speaker. Used to anchor the transfer marker at the structural boundary
// even if the caller speaks first after the handoff.
export function isAfterTransferBoundary(
    turn: SotTranscriptTurnDisplay,
    postTransferSotStartMs: number | null,
) {
    return (
        postTransferSotStartMs !== null &&
        typeof turn.start_ms === 'number' &&
        turn.start_ms > postTransferSotStartMs
    );
}

// True when `turn` is a post-transfer agent turn — used to relabel the
// agent (e.g. "Representative" instead of "Phoebe") and to keep
// pre- and post-transfer agent turns from being merged in condensed view.
export function isPostTransferSotTurn(
    turn: SotTranscriptTurnDisplay,
    postTransferSotStartMs: number | null,
) {
    return (
        turn.speaker === 'agent' &&
        isAfterTransferBoundary(turn, postTransferSotStartMs)
    );
}

const SOT_TURN_MERGE_GAP_MS = 2_000;

function getMergedConfidence(
    previous: SotTranscriptTurnDisplay,
    current: SotTranscriptTurnDisplay,
) {
    const confidenceValues = [previous.confidence, current.confidence].filter(
        (confidence): confidence is number => typeof confidence === 'number',
    );
    if (confidenceValues.length === 0) {
        return null;
    }

    return (
        confidenceValues.reduce((total, confidence) => total + confidence, 0) /
        confidenceValues.length
    );
}

function canMergeSotTurns(
    previous: SotTranscriptTurnDisplay,
    current: SotTranscriptTurnDisplay,
    postTransferSotStartMs: number | null,
) {
    if (previous.speaker !== current.speaker) {
        return false;
    }
    if (previous.channel !== current.channel) {
        return false;
    }
    // Keep turns that straddle the transfer boundary separate so the
    // transcript can still anchor a transfer marker between them, even
    // when both turns are from the caller (or any non-agent speaker).
    if (
        isAfterTransferBoundary(previous, postTransferSotStartMs) !==
        isAfterTransferBoundary(current, postTransferSotStartMs)
    ) {
        return false;
    }
    if (
        typeof previous.end_ms !== 'number' ||
        typeof current.start_ms !== 'number'
    ) {
        return false;
    }

    return current.start_ms - previous.end_ms < SOT_TURN_MERGE_GAP_MS;
}

export function condenseSotTranscriptTurns(
    turns: ReadonlyArray<SotTranscriptTurnDisplay>,
    postTransferSotStartMs: number | null,
): Array<SotTranscriptTurnDisplay> {
    const mergedTurns: Array<SotTranscriptTurnDisplay> = [];

    for (const turn of turns) {
        const previous = mergedTurns[mergedTurns.length - 1];
        if (
            previous &&
            canMergeSotTurns(previous, turn, postTransferSotStartMs)
        ) {
            mergedTurns[mergedTurns.length - 1] = {
                ...previous,
                text: `${previous.text} ${turn.text}`.trim(),
                end_ms: turn.end_ms ?? previous.end_ms,
                confidence: getMergedConfidence(previous, turn),
                words: [...(previous.words ?? []), ...(turn.words ?? [])],
            };
            continue;
        }

        mergedTurns.push(turn);
    }

    return mergedTurns;
}
